import type {
  ActiveMedication,
  HealthEntry,
  LabResult,
  MedicationHistory
} from '@/types/health'

type HypothesisStrength = 'forte' | 'possible' | 'a_verifier'

type ClinicalLabResult = LabResult & {
  normalized_unit?: string | null
  normalized_value?: number | null
  normalized_reference_min?: number | null
  normalized_reference_max?: number | null
}

export interface ClinicalHypothesis {
  id: string
  label: string
  strength: HypothesisStrength
  score: number
  summary: string
  evidence_for: string[]
  evidence_against: string[]
  missing_checks: string[]
  appointment_questions: string[]
}

export interface ClinicalCorrelation {
  label: string
  detail: string
}

export interface ClinicalDashboardSummary {
  overview: string
  watch_items: string[]
  action_items: string[]
  correlations: ClinicalCorrelation[]
  hypotheses: ClinicalHypothesis[]
}

interface ClinicalContext {
  healthEntries: HealthEntry[]
  activeMedications: ActiveMedication[]
  medicationHistory: MedicationHistory[]
  labResults: ClinicalLabResult[]
  recentEntries: HealthEntry[]
  symptomText: string
  bleedingCount: number
  urgentCount: number
  highPainCount: number
  lowEnergyCount: number
  averagePain: number | null
  averageEnergy: number | null
}

function normalizeText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function uniqueItems(items: Array<string | null | undefined>): string[] {
  return [...new Set(items.map((item) => item?.trim()).filter(Boolean) as string[])]
}

function sortByDateDesc<T extends { test_date: string }>(items: T[]): T[] {
  return [...items].sort((left, right) => right.test_date.localeCompare(left.test_date))
}

function buildSymptomText(entries: HealthEntry[]): string {
  return entries
    .flatMap((entry) => [
      entry.summary,
      entry.notes,
      ...(entry.symptoms || []),
      ...(entry.triggers || [])
    ])
    .filter(Boolean)
    .join(' ')
}

function average(values: number[]): number | null {
  if (values.length === 0) {
    return null
  }

  return values.reduce((total, value) => total + value, 0) / values.length
}

function isRecent(date: string, daysBack: number): boolean {
  const cutoff = Date.now() - daysBack * 24 * 60 * 60 * 1000
  return new Date(date).getTime() >= cutoff
}

function buildClinicalContext(input: {
  healthEntries: HealthEntry[]
  activeMedications: ActiveMedication[]
  medicationHistory: MedicationHistory[]
  labResults: LabResult[]
}): ClinicalContext {
  const recentEntries = input.healthEntries.filter((entry) => isRecent(entry.entry_date, 45))
  const symptomText = normalizeText(buildSymptomText(recentEntries))
  const bleedingCount = recentEntries.filter((entry) => entry.bleeding_present).length
  const urgentCount = recentEntries.filter((entry) => entry.urgent_alert).length
  const highPainValues = recentEntries
    .map((entry) => entry.pain_level)
    .filter((value): value is number => typeof value === 'number' && value >= 6)
  const lowEnergyValues = recentEntries
    .map((entry) => entry.energy_level)
    .filter((value): value is number => typeof value === 'number' && value <= 4)

  return {
    healthEntries: input.healthEntries,
    activeMedications: input.activeMedications,
    medicationHistory: input.medicationHistory,
    labResults: sortByDateDesc(input.labResults as ClinicalLabResult[]),
    recentEntries,
    symptomText,
    bleedingCount,
    urgentCount,
    highPainCount: highPainValues.length,
    lowEnergyCount: lowEnergyValues.length,
    averagePain: average(
      recentEntries
        .map((entry) => entry.pain_level)
        .filter((value): value is number => typeof value === 'number')
    ),
    averageEnergy: average(
      recentEntries
        .map((entry) => entry.energy_level)
        .filter((value): value is number => typeof value === 'number')
    )
  }
}

function findLatestLabResult(
  results: ClinicalLabResult[],
  patterns: RegExp[]
): ClinicalLabResult | null {
  return (
    results.find((result) => {
      const normalizedName = normalizeText(result.test_name)
      return patterns.some((pattern) => pattern.test(normalizedName))
    }) || null
  )
}

function getComparableValue(result: ClinicalLabResult | null): number | null {
  if (!result) {
    return null
  }

  return result.normalized_value ?? result.result_value
}

function getComparableReferenceMin(result: ClinicalLabResult | null): number | null {
  if (!result) {
    return null
  }

  return result.normalized_reference_min ?? result.reference_min
}

function getComparableReferenceMax(result: ClinicalLabResult | null): number | null {
  if (!result) {
    return null
  }

  return result.normalized_reference_max ?? result.reference_max
}

function getResultState(result: ClinicalLabResult | null): 'low' | 'high' | 'normal' | 'unknown' {
  if (!result) {
    return 'unknown'
  }

  if (result.abnormal_flag === 'L' || result.abnormal_flag === 'LL') {
    return 'low'
  }

  if (result.abnormal_flag === 'H' || result.abnormal_flag === 'HH') {
    return 'high'
  }

  const value = getComparableValue(result)
  const referenceMin = getComparableReferenceMin(result)
  const referenceMax = getComparableReferenceMax(result)

  if (value === null) {
    return 'unknown'
  }

  if (referenceMin !== null && value < referenceMin) {
    return 'low'
  }

  if (referenceMax !== null && value > referenceMax) {
    return 'high'
  }

  if (referenceMin !== null || referenceMax !== null) {
    return 'normal'
  }

  return result.is_abnormal ? 'high' : 'unknown'
}

function formatLabValue(value: number | null, unit: string | null | undefined): string {
  if (value === null || Number.isNaN(value)) {
    return '--'
  }

  const rounded =
    Math.abs(value) >= 10
      ? Number.isInteger(value)
        ? value.toString()
        : value.toFixed(1).replace(/\.0$/, '')
      : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')

  return unit ? `${rounded} ${unit}` : rounded
}

function describeResult(label: string, result: ClinicalLabResult | null): string | null {
  if (!result) {
    return null
  }

  return `${label}: ${formatLabValue(getComparableValue(result), result.normalized_unit || result.unit)}`
}

function hasSymptoms(symptomText: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(symptomText))
}

function strengthFromScore(score: number): HypothesisStrength {
  if (score >= 6) return 'forte'
  if (score >= 3) return 'possible'
  return 'a_verifier'
}

function createHypothesis(
  id: string,
  label: string,
  summary: string,
  score: number,
  evidenceFor: string[],
  evidenceAgainst: string[],
  missingChecks: string[],
  appointmentQuestions: string[]
): ClinicalHypothesis | null {
  if (score <= 0 && evidenceFor.length === 0 && missingChecks.length === 0) {
    return null
  }

  return {
    id,
    label,
    strength: strengthFromScore(score),
    score,
    summary,
    evidence_for: evidenceFor.slice(0, 4),
    evidence_against: evidenceAgainst.slice(0, 3),
    missing_checks: missingChecks.slice(0, 4),
    appointment_questions: appointmentQuestions.slice(0, 3)
  }
}

function buildIronHypothesis(context: ClinicalContext): ClinicalHypothesis | null {
  const ferritine = findLatestLabResult(context.labResults, [/^ferritine$/])
  const hemoglobine = findLatestLabResult(context.labResults, [/^hemoglobine$/])
  const vgm = findLatestLabResult(context.labResults, [/^vgm$/])
  const tcmh = findLatestLabResult(context.labResults, [/^tcmh$/])
  const evidenceFor = uniqueItems([
    getResultState(ferritine) === 'low' ? describeResult('Ferritine basse', ferritine) : null,
    getResultState(hemoglobine) === 'low' ? describeResult('Hemoglobine basse', hemoglobine) : null,
    getResultState(vgm) === 'low' ? describeResult('VGM bas', vgm) : null,
    getResultState(tcmh) === 'low' ? describeResult('TCMH basse', tcmh) : null,
    context.bleedingCount > 0 ? 'Saignements notes dans le journal recent.' : null,
    context.lowEnergyCount > 0 ? 'Fatigue / baisse d energie recurrente.' : null
  ])
  const evidenceAgainst = uniqueItems([
    getResultState(ferritine) === 'normal' ? 'Ferritine recente dans les normes.' : null,
    getResultState(hemoglobine) === 'normal' ? 'Hemoglobine recente dans les normes.' : null
  ])
  const missingChecks = uniqueItems([
    ferritine ? null : 'Ferritine',
    hemoglobine ? null : 'NFS / hemoglobine',
    vgm ? null : 'Indices erythrocytaires',
    findLatestLabResult(context.labResults, [/^vitamine b12$/]) ? null : 'Vitamine B12 / folates'
  ])

  let score = 0
  if (getResultState(ferritine) === 'low') score += 4
  if (getResultState(hemoglobine) === 'low') score += 3
  if (getResultState(vgm) === 'low' || getResultState(tcmh) === 'low') score += 2
  if (context.bleedingCount > 0) score += 2
  if (context.lowEnergyCount > 0) score += 1

  return createHypothesis(
    'iron-deficiency',
    'Carence martiale / anemie',
    'Croiser reserves martiales, NFS et saignements pour confirmer une carence ou une anemie.',
    score,
    evidenceFor,
    evidenceAgainst,
    missingChecks,
    uniqueItems([
      'Les pertes sanguines peuvent-elles expliquer la fatigue ou les anomalies de la NFS ?',
      'Faut-il completer par ferritine, transferrine, B12 ou folates ?'
    ])
  )
}

function buildDigestiveInflammationHypothesis(context: ClinicalContext): ClinicalHypothesis | null {
  const crp = findLatestLabResult(context.labResults, [/^crp$/])
  const calprotectine = findLatestLabResult(context.labResults, [/^calprotectine$/])
  const albumine = findLatestLabResult(context.labResults, [/^albumine$/])
  const plaquettes = findLatestLabResult(context.labResults, [/^plaquettes$/])
  const leucocytes = findLatestLabResult(context.labResults, [/^leucocytes$/])
  const digestiveSymptoms = hasSymptoms(context.symptomText, [
    /diarrh/,
    /selles/,
    /digest/,
    /abdo/,
    /ballonn/,
    /colique/,
    /rectorrag/,
    /douleur intest/
  ])

  const evidenceFor = uniqueItems([
    getResultState(calprotectine) === 'high'
      ? describeResult('Calprotectine elevee', calprotectine)
      : null,
    getResultState(crp) === 'high' ? describeResult('CRP elevee', crp) : null,
    getResultState(albumine) === 'low' ? describeResult('Albumine basse', albumine) : null,
    getResultState(plaquettes) === 'high' ? describeResult('Plaquettes elevees', plaquettes) : null,
    getResultState(leucocytes) === 'high' ? describeResult('Leucocytes eleves', leucocytes) : null,
    digestiveSymptoms ? 'Symptomes digestifs recents dans le journal.' : null,
    context.bleedingCount > 0 ? 'Saignements recents associes.' : null
  ])
  const evidenceAgainst = uniqueItems([
    getResultState(crp) === 'normal' ? 'CRP recente normale.' : null,
    getResultState(calprotectine) === 'normal' ? 'Calprotectine recente normale.' : null
  ])
  const missingChecks = uniqueItems([
    crp ? null : 'CRP',
    calprotectine ? null : 'Calprotectine fecale',
    albumine ? null : 'Albumine',
    findLatestLabResult(context.labResults, [/^ferritine$/, /^hemoglobine$/])
      ? null
      : 'NFS / ferritine'
  ])

  let score = 0
  if (getResultState(calprotectine) === 'high') score += 4
  if (getResultState(crp) === 'high') score += 3
  if (getResultState(albumine) === 'low') score += 2
  if (getResultState(plaquettes) === 'high' || getResultState(leucocytes) === 'high') score += 1
  if (digestiveSymptoms) score += 2
  if (context.bleedingCount > 0) score += 2

  return createHypothesis(
    'digestive-inflammation',
    'RCH / inflammation digestive',
    'Verifier si les symptomes et la biologie vont dans le sens d une inflammation digestive active.',
    score,
    evidenceFor,
    evidenceAgainst,
    missingChecks,
    uniqueItems([
      'Faut-il documenter une poussee par CRP, calprotectine et NFS ?',
      'Les saignements ou douleurs digestives concordent-ils avec la biologie ?'
    ])
  )
}

function buildSystemicInflammationHypothesis(context: ClinicalContext): ClinicalHypothesis | null {
  const crp = findLatestLabResult(context.labResults, [/^crp$/])
  const vs = findLatestLabResult(context.labResults, [/^vs$/])
  const plaquettes = findLatestLabResult(context.labResults, [/^plaquettes$/])
  const leucocytes = findLatestLabResult(context.labResults, [/^leucocytes$/])

  const evidenceFor = uniqueItems([
    getResultState(crp) === 'high' ? describeResult('CRP elevee', crp) : null,
    getResultState(vs) === 'high' ? describeResult('VS elevee', vs) : null,
    getResultState(plaquettes) === 'high' ? describeResult('Plaquettes elevees', plaquettes) : null,
    getResultState(leucocytes) === 'high' ? describeResult('Leucocytes eleves', leucocytes) : null,
    context.highPainCount > 0 ? 'Douleurs elevees repetes dans le journal.' : null,
    context.urgentCount > 0 ? 'Alerte(s) prioritaire(s) recentes dans le journal.' : null
  ])
  const evidenceAgainst = uniqueItems([
    getResultState(crp) === 'normal' ? 'CRP recente normale.' : null,
    getResultState(vs) === 'normal' ? 'VS recente normale.' : null
  ])
  const missingChecks = uniqueItems([
    crp ? null : 'CRP',
    vs ? null : 'VS',
    findLatestLabResult(context.labResults, [/^ferritine$/]) ? null : 'Ferritine',
    findLatestLabResult(context.labResults, [/^fibrinogene$/]) ? null : 'Fibrinogene si utile'
  ])

  let score = 0
  if (getResultState(crp) === 'high') score += 3
  if (getResultState(vs) === 'high') score += 2
  if (getResultState(plaquettes) === 'high' || getResultState(leucocytes) === 'high') score += 1
  if (context.highPainCount > 0) score += 1
  if (context.urgentCount > 0) score += 1

  return createHypothesis(
    'systemic-inflammation',
    'Inflammation systemique',
    'Mettre en regard douleur, marqueurs inflammatoires et contexte clinique global.',
    score,
    evidenceFor,
    evidenceAgainst,
    missingChecks,
    uniqueItems([
      'Les anomalies inflammatoires orientent-elles vers une cause digestive, gyneco ou auto-immune ?',
      'Quels examens complementaires seraient les plus utiles si les symptomes persistent ?'
    ])
  )
}

function buildHormonalHypothesis(context: ClinicalContext): ClinicalHypothesis | null {
  const tsh = findLatestLabResult(context.labResults, [/^tsh$/])
  const t4 = findLatestLabResult(context.labResults, [/^t4 libre$/])
  const t3 = findLatestLabResult(context.labResults, [/^t3 libre$/])

  const evidenceFor = uniqueItems([
    getResultState(tsh) !== 'normal' && getResultState(tsh) !== 'unknown'
      ? describeResult('TSH a discuter', tsh)
      : null,
    getResultState(t4) !== 'normal' && getResultState(t4) !== 'unknown'
      ? describeResult('T4 libre a discuter', t4)
      : null,
    getResultState(t3) !== 'normal' && getResultState(t3) !== 'unknown'
      ? describeResult('T3 libre a discuter', t3)
      : null,
    context.lowEnergyCount > 0 ? 'Fatigue persistante recente.' : null
  ])
  const evidenceAgainst = uniqueItems([
    getResultState(tsh) === 'normal' ? 'TSH recente normale.' : null
  ])
  const missingChecks = uniqueItems([
    tsh ? null : 'TSH',
    t4 ? null : 'T4 libre',
    t3 ? null : 'T3 libre'
  ])

  let score = 0
  if (getResultState(tsh) !== 'normal' && getResultState(tsh) !== 'unknown') score += 4
  if (getResultState(t4) !== 'normal' && getResultState(t4) !== 'unknown') score += 2
  if (getResultState(t3) !== 'normal' && getResultState(t3) !== 'unknown') score += 1
  if (context.lowEnergyCount > 0) score += 1

  return createHypothesis(
    'thyroid-hormonal',
    'Axe thyroidien / hormonal',
    'Verifier si fatigue et variations biologiques justifient un point thyroidien ou hormonal.',
    score,
    evidenceFor,
    evidenceAgainst,
    missingChecks,
    uniqueItems([
      'Faut-il completer ou recontroler le bilan thyroidien selon les symptomes ?'
    ])
  )
}

function buildNutritionalHypothesis(context: ClinicalContext): ClinicalHypothesis | null {
  const albumine = findLatestLabResult(context.labResults, [/^albumine$/])
  const vitamineD = findLatestLabResult(context.labResults, [/^vitamine d$/])
  const vitamineB12 = findLatestLabResult(context.labResults, [/^vitamine b12$/])
  const folates = findLatestLabResult(context.labResults, [/^folates?$/])

  const evidenceFor = uniqueItems([
    getResultState(albumine) === 'low' ? describeResult('Albumine basse', albumine) : null,
    getResultState(vitamineD) === 'low' ? describeResult('Vitamine D basse', vitamineD) : null,
    getResultState(vitamineB12) === 'low' ? describeResult('Vitamine B12 basse', vitamineB12) : null,
    getResultState(folates) === 'low' ? describeResult('Folates bas', folates) : null,
    hasSymptoms(context.symptomText, [/digest/, /diarrh/, /selles/])
      ? 'Contexte digestif pouvant majorer les carences.'
      : null
  ])
  const evidenceAgainst = uniqueItems([
    getResultState(albumine) === 'normal' ? 'Albumine recente normale.' : null
  ])
  const missingChecks = uniqueItems([
    albumine ? null : 'Albumine',
    vitamineD ? null : 'Vitamine D',
    vitamineB12 ? null : 'Vitamine B12',
    folates ? null : 'Folates'
  ])

  let score = 0
  if (getResultState(albumine) === 'low') score += 3
  if (getResultState(vitamineD) === 'low') score += 2
  if (getResultState(vitamineB12) === 'low') score += 2
  if (getResultState(folates) === 'low') score += 2
  if (hasSymptoms(context.symptomText, [/digest/, /diarrh/, /selles/])) score += 1

  return createHypothesis(
    'nutrition-malabsorption',
    'Retentissement nutritionnel / carences',
    'Verifier si le terrain digestif retentit sur les vitamines, proteines et reserves.',
    score,
    evidenceFor,
    evidenceAgainst,
    missingChecks,
    uniqueItems([
      'Faut-il suivre vitamines, albumine et reserves martiales de facon rapprochee ?'
    ])
  )
}

function buildCorrelations(context: ClinicalContext): ClinicalCorrelation[] {
  const ferritine = findLatestLabResult(context.labResults, [/^ferritine$/])
  const hemoglobine = findLatestLabResult(context.labResults, [/^hemoglobine$/])
  const crp = findLatestLabResult(context.labResults, [/^crp$/])
  const calprotectine = findLatestLabResult(context.labResults, [/^calprotectine$/])
  const tsh = findLatestLabResult(context.labResults, [/^tsh$/])
  const recentTreatmentChange = context.medicationHistory.find(
    (entry) =>
      ['started', 'modified', 'paused', 'resumed', 'stopped', 'completed'].includes(entry.action) &&
      isRecent(entry.change_date, 120)
  )
  const digestiveSymptoms = hasSymptoms(context.symptomText, [
    /diarrh/,
    /selles/,
    /digest/,
    /abdo/,
    /ballonn/,
    /colique/,
    /rectorrag/
  ])

  return uniqueItems([
    context.bleedingCount > 0 &&
    (getResultState(ferritine) === 'low' || getResultState(hemoglobine) === 'low')
      ? 'Saignements + carence martiale / anemie'
      : null,
    context.lowEnergyCount > 0 &&
    (getResultState(ferritine) === 'low' || getResultState(hemoglobine) === 'low')
      ? 'Fatigue + carence martiale / anemie'
      : null,
    context.highPainCount > 0 && getResultState(crp) === 'high'
      ? 'Douleurs elevees + CRP elevee'
      : null,
    context.highPainCount > 0 && getResultState(crp) === 'normal'
      ? 'Douleurs importantes avec CRP normale'
      : null,
    digestiveSymptoms &&
    (getResultState(crp) === 'high' || getResultState(calprotectine) === 'high')
      ? 'Symptomes digestifs + inflammation biologique'
      : null,
    context.lowEnergyCount > 0 &&
    getResultState(tsh) !== 'normal' &&
    getResultState(tsh) !== 'unknown'
      ? 'Fatigue + bilan thyroidien a recouper'
      : null,
    recentTreatmentChange &&
    (context.highPainCount > 0 || context.lowEnergyCount > 0 || context.bleedingCount > 0)
      ? `Symptomes apres changement de traitement: ${recentTreatmentChange.action}`
      : null
  ]).map((label) => {
    if (label === 'Saignements + carence martiale / anemie') {
      return {
        label,
        detail: 'Les episodes de saignement recents peuvent expliquer une partie de la fatigue et des anomalies hematologiques.'
      }
    }

    if (label === 'Fatigue + carence martiale / anemie') {
      return {
        label,
        detail: 'La fatigue recente merite d etre recoupee avec les reserves martiales et l hemoglobine.'
      }
    }

    if (label === 'Douleurs elevees + CRP elevee') {
      return {
        label,
        detail: 'La douleur recente semble evoluer en parallele d un signal inflammatoire biologique.'
      }
    }

    if (label === 'Douleurs importantes avec CRP normale') {
      return {
        label,
        detail: 'La symptomatologie douloureuse ne parait pas exclusivement expliquee par une inflammation biologique visible.'
      }
    }

    if (label === 'Symptomes digestifs + inflammation biologique') {
      return {
        label,
        detail: 'Les symptomes digestifs recents semblent evoluer en parallele d un signal inflammatoire de laboratoire.'
      }
    }

    if (label === 'Fatigue + bilan thyroidien a recouper') {
      return {
        label,
        detail: 'La fatigue recente ne doit pas etre lue uniquement sous l angle inflammatoire ou martial.'
      }
    }

    if (label.startsWith('Symptomes apres changement de traitement:')) {
      return {
        label: 'Traitement recent',
        detail: 'Une modification de traitement recente merite d etre recoupee avec l evolution des symptomes et des bilans.'
      }
    }

    return {
      label: 'Traitement recent',
      detail: 'Un changement de traitement recent merite d etre recale sur la chronologie des symptomes et des bilans.'
    }
  })
}

export function buildClinicalDashboardSummary(input: {
  healthEntries: HealthEntry[]
  activeMedications: ActiveMedication[]
  medicationHistory: MedicationHistory[]
  labResults: LabResult[]
}): ClinicalDashboardSummary {
  const context = buildClinicalContext(input)

  if (
    context.healthEntries.length === 0 &&
    context.activeMedications.length === 0 &&
    context.labResults.length === 0
  ) {
    return {
      overview: 'Ajoutez quelques donnees pour faire ressortir des pistes cliniques utiles.',
      watch_items: [],
      action_items: ['Completer le journal quotidien ou importer un bilan recent.'],
      correlations: [],
      hypotheses: []
    }
  }

  const hypotheses = [
    buildDigestiveInflammationHypothesis(context),
    buildIronHypothesis(context),
    buildSystemicInflammationHypothesis(context),
    buildHormonalHypothesis(context),
    buildNutritionalHypothesis(context)
  ]
    .filter(Boolean)
    .sort((left, right) => (right?.score || 0) - (left?.score || 0))
    .slice(0, 4) as ClinicalHypothesis[]

  const topHypothesis = hypotheses[0] || null
  const watchItems = uniqueItems([
    context.urgentCount > 0 ? 'Alerte recente dans le journal quotidien.' : null,
    context.bleedingCount > 0 ? 'Saignements recents a recouper avec la biologie.' : null,
    context.averagePain !== null && context.averagePain >= 6 ? 'Douleur recente elevee.' : null,
    context.averageEnergy !== null && context.averageEnergy <= 4 ? 'Energie recente basse.' : null,
    ...hypotheses.flatMap((hypothesis) => hypothesis.evidence_for.slice(0, 1))
  ]).slice(0, 5)
  const actionItems = uniqueItems([
    ...hypotheses.flatMap((hypothesis) => hypothesis.missing_checks.slice(0, 2)),
    ...hypotheses.flatMap((hypothesis) => hypothesis.appointment_questions.slice(0, 1)),
    context.activeMedications.length === 0 && context.healthEntries.length > 0
      ? 'Revoir la strategie therapeutique actuelle si les symptomes restent actifs.'
      : null
  ]).slice(0, 6)

  const overview = topHypothesis
    ? `Piste principale a discuter: ${topHypothesis.label.toLowerCase()}. ${topHypothesis.summary}`
    : 'Les donnees commencent a decrire la situation, mais restent encore trop partielles pour faire ressortir une piste nette.'

  return {
    overview,
    watch_items: watchItems,
    action_items: actionItems,
    correlations: buildCorrelations(context).slice(0, 4),
    hypotheses
  }
}
